/**
 * Adapter: organisation object definition (data) → regular sketch-object
 * type. Everything downstream (PointoramaSketchObject rendering, resize
 * handles, gizmo binding, property panel, DXF blocks, icons) consumes the
 * resulting {@link RegisteredSketchObjectType} exactly like a built-in.
 */

import { customSketchObjectTypeId, isFormulaHandlePoint, type ObjectDefinitionSpec, type ObjectHandlePoint, type ObjectParam } from 'shared-types';
import { getSketchObjectDepthProperty, getSketchObjectWidthProperty } from '@pointorama/potree-shared';
import { defineSketchObjectType, type RegisteredSketchObjectType } from '../registry';
import { getNumberParamProperty, readParamValues, type ParamSchema, type ParamSpec } from '../paramSchema';
import { SketchObjectAnchorKind, type SketchObjectDepthHandleSpec, type SketchObjectFrame, type SketchObjectResizeHandleLayout } from '../types';
import { evaluateCustomGlyph, evaluationVars, expressionValue, resolveCustomParams, type CustomParamValues } from './evaluateCustomGlyph';
import { customSyncWrites, depthHandleDrag, repeatSyncWrites, type LiveObjectState, type SyncWrite } from './customSync';
import { t } from '../../translation/markers';

/** What the client needs of a published definition to register it. */
export interface CustomObjectDefinitionInput {
  definitionId: string;
  name: string;
  /** Built-in SketchObjectCategory value or an organisation category id. */
  categoryId: string;
  spec: ObjectDefinitionSpec;
  /** Extra search keywords of the library picker. */
  tags?: readonly string[];
  /** False for archived definitions: placed objects keep rendering, the picker hides it. */
  listed?: boolean;
  /** Read URL of the model GLB (spec.model3d.assetId), when the object has one. */
  modelUrl?: string;
}

/**
 * Param key of a placed object's 3D height — added automatically to types
 * with a 3D model (the 2D glyph has no height). Not a valid user param key
 * (they start with a letter), so it can't collide.
 */
export const MODEL_HEIGHT_PARAM = '__height';

function toParamSpec(p: ObjectParam): ParamSpec {
  const order = p.order !== undefined ? { order: p.order } : {};
  switch (p.kind) {
    case 'float':
      return { kind: 'float', label: p.label, default: p.default, min: p.min, max: p.max, decimals: p.decimals, distance: p.distance, ...order };
    case 'int':
      return { kind: 'int', label: p.label, default: p.default, min: p.min, max: p.max, ...order };
    case 'boolean':
      return { kind: 'boolean', label: p.label, default: p.default, ...order };
    case 'select':
      return { kind: 'select', label: p.label, default: p.default, options: p.options, ...order };
  }
}

/**
 * Param key of a placed dimension's offset — how far its line sits off the
 * measured points (m, along the frame normal; the `offset` expression
 * variable). Added automatically to dimension types; set by the dimension
 * tool's third click, editable in the panel.
 */
export const DIMENSION_OFFSET_PARAM = '__offset';

export function specToParamSchema(spec: ObjectDefinitionSpec): ParamSchema {
  const schema: ParamSchema = {};
  for (const p of spec.params) schema[p.key] = toParamSpec(p);
  if (spec.model3d) {
    // Right after the shared width/depth rows (10-11).
    schema[MODEL_HEIGHT_PARAM] = { kind: 'float', label: t('height'), default: spec.defaultSize.height, min: 0.05, max: 50, decimals: 2, distance: true, order: 11.6 };
  }
  if (spec.dimension) schema[DIMENSION_OFFSET_PARAM] = { kind: 'float', label: t('offset'), default: 0, min: -100, max: 100, decimals: 2, distance: true, order: 11.6 };
  return schema;
}

const finiteOr0 = (value: unknown): number => (typeof value === 'number' && Number.isFinite(value) ? value : 0);

/** The offset a dimension's params carry (0 when unset). */
export function dimensionOffsetOf(params: CustomParamValues): number {
  return finiteOr0(params[DIMENSION_OFFSET_PARAM]);
}

/** A placed dimension's offset (0 for other objects). */
export function placedDimensionOffset(type: RegisteredSketchObjectType, objectId: string): number {
  return type.dimension ? finiteOr0(type.getParamValues(objectId)[DIMENSION_OFFSET_PARAM]) : 0;
}

/** A placed object's live width, depth and (resolved) params, read from the property service. */
function readLiveState(typeId: string, spec: ObjectDefinitionSpec, objectId: string): LiveObjectState {
  return {
    width: getSketchObjectWidthProperty(objectId).value ?? spec.defaultSize.width,
    depth: getSketchObjectDepthProperty(objectId).value ?? spec.defaultSize.depth,
    params: resolveCustomParams(spec, readParamValues(typeId, specToParamSchema(spec), objectId)),
  };
}

/** Write one sync answer; `echo` marks a count whose notification must not re-even the step. */
function applyWrite(typeId: string, objectId: string, write: SyncWrite): void {
  if (write.key === 'depth') getSketchObjectDepthProperty(objectId).value = write.value;
  else getNumberParamProperty(typeId, write.key, objectId).value = write.value;
}

export function customHandleLayout(typeId: string, spec: ObjectDefinitionSpec): ((frame: SketchObjectFrame, params: CustomParamValues) => SketchObjectResizeHandleLayout) | undefined {
  const handles = spec.handles;
  if (!handles || spec.anchor !== 'LINE') return undefined;
  return (frame, params) => {
    const vars = evaluationVars(spec, frame.width, frame.depth, resolveCustomParams(spec, params));
    // Fractions of the live footprint, or formulas giving the live position.
    const point = (p: ObjectHandlePoint): readonly [number, number] =>
      isFormulaHandlePoint(p) ? [expressionValue(p.u, vars), expressionValue(p.n, vars)] : [p[0] * frame.width, p[1] * frame.depth];
    const depth = handles.depth?.map((d): SketchObjectDepthHandleSpec => {
      const [u, n] = d.at ? point(d.at) : [(d.u ?? 0) * frame.width, (d.side * frame.depth) / 2];
      const assign = d.assign;
      if (!assign) return { u, n, side: d.side };
      return {
        u,
        n,
        side: d.side,
        // Sets the params from the pulled distance; the depth follows (see depthHandleDrag).
        apply: (objectId, pulled) => {
          const drag = depthHandleDrag(spec, assign, readLiveState(typeId, spec, objectId), pulled);
          for (const write of drag.writes) applyWrite(typeId, objectId, write);
          return drag.depth;
        },
      };
    });
    return {
      ...(handles.start ? { start: point(handles.start) } : {}),
      ...(handles.end ? { end: point(handles.end) } : {}),
      ...(depth ? { depth } : {}),
    };
  };
}

/**
 * Step syncs + the linked depth, then the linear repeats' pitch ↔ count
 * pairs (all pure, customSync.ts) — each write re-enters through the
 * property service; `settleSync` replays the same cascade for Test mode.
 */
function makeSync(typeId: string, spec: ObjectDefinitionSpec): ((objectId: string, changedKey: string) => void) | undefined {
  const hasRepeatPairs = spec.groups.some((g) => g.repeat?.kind === 'linear' && g.repeat.pitch && g.repeat.count && 'param' in g.repeat.pitch && 'param' in g.repeat.count);
  if (!spec.stepSyncs?.length && !spec.depthLink && !hasRepeatPairs) return undefined;
  // True while a count written as an echo notifies (notify is synchronous).
  let countEcho = false;
  const run = (objectId: string, writes: (live: LiveObjectState, echo: boolean) => SyncWrite[]) => {
    let live: LiveObjectState;
    try {
      live = readLiveState(typeId, spec, objectId);
    } catch {
      return; // property service not ready
    }
    for (const write of writes(live, countEcho)) {
      const previous = countEcho;
      countEcho = write.countEcho === true;
      try {
        applyWrite(typeId, objectId, write);
      } finally {
        countEcho = previous;
      }
    }
  };
  return (objectId, changedKey) => {
    run(objectId, (live, echo) => customSyncWrites(spec, changedKey, live, echo));
    run(objectId, (live, echo) => repeatSyncWrites(spec, changedKey, live, echo));
  };
}

/** A sketch-object type drawn from a data spec under `id` (organisation definitions and the data-only built-ins). */
export function buildDataSketchObjectType(id: string, label: string, category: string, spec: ObjectDefinitionSpec): RegisteredSketchObjectType {
  const resizeHandles = customHandleLayout(id, spec);
  const hasSelectionLayer = spec.shapes.some((s) => s.layer === 'selection');
  return defineSketchObjectType<ParamSchema>({
    id,
    label,
    category,
    anchor: spec.anchor === 'LINE' ? SketchObjectAnchorKind.LINE : SketchObjectAnchorKind.POINT,
    constraint: spec.constraint,
    defaultSize: { width: spec.defaultSize.width, depth: spec.defaultSize.depth },
    params: specToParamSchema(spec),
    glyph: { kind: 'parametric', build: (frame, params) => evaluateCustomGlyph(spec, frame, params, 'glyph', { offset: dimensionOffsetOf(params) }) },
    drawLikeWall: spec.drawLikeWall,
    dimension: spec.dimension !== undefined,
    ...(hasSelectionLayer ? { selectionGlyph: (frame: SketchObjectFrame, params: CustomParamValues) => evaluateCustomGlyph(spec, frame, params, 'selection', { offset: dimensionOffsetOf(params) }) } : {}),
    ...(resizeHandles ? { resizeHandles } : {}),
    syncParams: makeSync(id, spec),
  });
}

export function buildCustomSketchObjectType(input: CustomObjectDefinitionInput): RegisteredSketchObjectType {
  const { spec } = input;
  const type = buildDataSketchObjectType(customSketchObjectTypeId(input.definitionId), input.name, input.categoryId, spec);
  return { ...type, listed: input.listed ?? true, custom: { definitionId: input.definitionId, spec, tags: input.tags ?? [], modelUrl: input.modelUrl } };
}
